import { useState, useMemo } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { COUNTRIES } from "@/data/countries";

/**
 * CountrySelect
 * Searchable country picker. Two visual variants:
 *   - variant="dial"     → trigger shows "🇷🇸 +381" (compact, for phone prefix)
 *   - variant="country"  → trigger shows "🇷🇸 Serbia" (full, for address field)
 *
 * Props:
 *   value     – ISO-2 country code (e.g. "RS"), or "" for none
 *   onChange  – (code) => void
 *   variant   – "dial" | "country"
 *   lang      – "en" | "sr"  (controls localized country names)
 *   placeholder
 *   testid
 */
export function CountrySelect({
  value,
  onChange,
  variant = "country",
  lang = "en",
  placeholder,
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = COUNTRIES.find((c) => c.code === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => {
      const name = (lang === "sr" ? c.sr : c.en).toLowerCase();
      return (
        name.includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.replace("+", "").includes(q.replace("+", ""))
      );
    });
  }, [query, lang]);

  const triggerLabel = () => {
    if (!selected)
      return (
        <span className="text-[#9B9DA4] text-sm">
          {placeholder ||
            (lang === "sr"
              ? variant === "dial"
                ? "Kôd"
                : "Izaberite zemlju"
              : variant === "dial"
              ? "Code"
              : "Select country")}
        </span>
      );
    if (variant === "dial") {
      return (
        <span className="inline-flex items-center gap-1.5 text-[#0A0A0F]">
          <span className="text-lg leading-none">{selected.flag}</span>
          <span className="font-mono text-sm">{selected.dial}</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-2 text-[#0A0A0F] truncate">
        <span className="text-lg leading-none">{selected.flag}</span>
        <span className="text-sm truncate">
          {lang === "sr" ? selected.sr : selected.en}
        </span>
      </span>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={[
            "w-full inline-flex items-center justify-between gap-2",
            "px-3 py-3 rounded-xl bg-white border border-[#E8E7E2]",
            "hover:border-[#0A0A0F]/40 focus:border-[#0A0A0F] focus:outline-none transition-colors",
            "min-h-[46px]",
            className,
          ].join(" ")}
        >
          <span className="min-w-0 flex-1 text-left">{triggerLabel()}</span>
          <ChevronDown size={14} className="shrink-0 text-[#62646B]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className={[
          // Desktop: anchored popover under the trigger.
          "p-0 border-[#E8E7E2] shadow-lg",
          "sm:rounded-2xl sm:w-[min(360px,calc(100vw-2rem))]",
          // Mobile (<sm): pinned full-width sheet at the bottom of the screen
          // so the long country list is easy to thumb-scroll.
          "max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:left-0 max-sm:right-0",
          "max-sm:w-screen max-sm:max-w-none max-sm:rounded-t-2xl max-sm:rounded-b-none",
          "max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!top-auto",
          "max-sm:border-t max-sm:border-b-0 max-sm:border-x-0",
          "max-sm:p-0",
        ].join(" ")}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#E8E7E2]">
          <Search size={14} className="text-[#62646B]" />
          <input
            autoFocus
            data-testid={testid ? `${testid}-search` : undefined}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              lang === "sr"
                ? "Pretraga (ime, +kôd, ISO)"
                : "Search (name, +code, ISO)"
            }
            className="w-full bg-transparent text-sm outline-none placeholder:text-[#9B9DA4] text-[#0A0A0F]"
          />
        </div>
        <ul
          role="listbox"
          className="max-h-72 max-sm:max-h-[60vh] overflow-y-auto py-1"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-xs text-center text-[#62646B]">
              {lang === "sr" ? "Nema rezultata" : "No matches"}
            </li>
          )}
          {filtered.map((c) => {
            const isSelected = c.code === value;
            return (
              <li key={c.code} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  data-testid={
                    testid ? `${testid}-option-${c.code}` : undefined
                  }
                  onClick={() => {
                    onChange(c.code);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#FAFAF7] transition-colors ${
                    isSelected ? "bg-[#FAFAF7]" : ""
                  }`}
                >
                  <span className="text-lg leading-none shrink-0">
                    {c.flag}
                  </span>
                  <span className="flex-1 text-sm truncate text-[#0A0A0F]">
                    {lang === "sr" ? c.sr : c.en}
                  </span>
                  <span className="font-mono text-xs text-[#62646B] shrink-0">
                    {c.dial}
                  </span>
                  {isSelected && (
                    <Check size={14} className="text-[#0A0A0F] shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
